export type InteractionParameters = Record<string, string | number | boolean>;
export type InteractionTracker = (event: string, parameters: InteractionParameters) => void;

/** Collect interactions in one document (including same-origin twin frames).
 * The caller owns consent and must remove this collector when consent is withdrawn.
 * Never collect link text, input values, URL queries or fragment contents.
 */
export function installInteractionAnalytics(doc: Document, track: InteractionTracker): () => void {
  const videos = new WeakMap<HTMLVideoElement, { source: string; started: boolean; milestones: Set<number>; lastTime: number }>();
  const removers: Array<() => void> = [];
  const on = (name: string, listener: EventListener) => {
    doc.addEventListener(name, listener, true);
    removers.push(() => doc.removeEventListener(name, listener, true));
  };
  const click: EventListener = (event) => {
    // nodeType works across iframe realms, unlike instanceof Element.
    const node = event.target as Element | null;
    const target = node?.nodeType === 1 ? node : node?.parentElement;
    if (!target) return;
    const link = target.closest('a[href]');
    if (link) {
      try {
        const url = new URL(link.getAttribute('href')!, doc.baseURI);
        const base = new URL(doc.baseURI);
        if (url.protocol === 'http:' || url.protocol === 'https:') {
          track('link_click', { link_url: url.origin + url.pathname, link_path: url.pathname, outbound: url.origin !== base.origin, download: link.hasAttribute('download') });
        } else if (url.protocol === 'mailto:' || url.protocol === 'tel:') {
          track('link_click', { link_type: url.protocol.slice(0, -1) });
        }
      } catch { /* Ignore malformed or unsupported links. */ }
      return;
    }
    const action = target.closest('[data-analytics-action]')?.getAttribute('data-analytics-action');
    // Explicit stable identifiers only; no arbitrary UI text.
    if (action && /^[a-z][a-z0-9_-]{0,79}$/i.test(action)) track('ui_interaction', { action });
  };
  on('click', click);
  const recordMedia = (video: HTMLVideoElement, type: string) => {
    let source = 'inline-video';
    try {
      const rawSource = video.currentSrc || video.getAttribute('src') || video.querySelector('source')?.getAttribute('src');
      const url = rawSource ? new URL(rawSource, doc.baseURI) : null;
      // Blob and data URLs may contain identifiers/content, so never retain them.
      if (url && /^https?:$/.test(url.protocol)) source = url.pathname.split('/').pop() || 'inline-video';
    } catch { /* Keep a neutral identifier. */ }
    // Explicit stable identifiers distinguish product films that share promo.mp4.
    const title = video.getAttribute('data-analytics-video-title');
    if (title && /^[a-z][a-z0-9_-]{0,79}$/i.test(title)) source = title;
    let state = videos.get(video);
    if (!state || state.source !== source) {
      state = { source, started: false, milestones: new Set(), lastTime: video.currentTime };
      videos.set(video, state);
    }
    const current = Number.isFinite(video.currentTime) ? Math.round(video.currentTime * 10) / 10 : 0;
    const duration = Number.isFinite(video.duration) ? Math.round(video.duration * 10) / 10 : 0;
    const params = { video_title: source, video_current_time: current, video_duration: duration };
    if (type === 'play') {
      if (!state.started) {
        track('video_start', params);
        state.started = true;
      }
      state.lastTime = video.currentTime;
    } else if (type === 'timeupdate') {
      // Seeking past a milestone does not count as watching it.
      if (state.started && !video.seeking && duration > 0 && video.currentTime - state.lastTime <= 5) {
        for (const percent of [25, 50, 75]) {
          const threshold = video.duration * percent / 100;
          if (!state.milestones.has(percent) && state.lastTime < threshold && video.currentTime >= threshold) {
            state.milestones.add(percent);
            track('video_progress', { ...params, video_percent: percent });
          }
        }
      }
      state.lastTime = video.currentTime;
    } else if (type === 'seeked') {
      state.lastTime = video.currentTime;
    } else if (type === 'pause' && state.started && !video.ended) {
      track('video_pause', params);
    } else if (type === 'ended' && state.started) {
      track('video_complete', { ...params, video_percent: 100 });
      state.started = false;
      state.milestones.clear();
      state.lastTime = 0;
    }
  };
  const media: EventListener = event => {
    const video = event.target as HTMLVideoElement | null;
    if (video?.tagName === 'VIDEO') recordMedia(video, event.type);
  };
  for (const name of ['play', 'timeupdate', 'seeked', 'pause', 'ended']) on(name, media);
  // Consent can arrive after playback started. Begin at the current position;
  // never reconstruct viewing events from before this collector was installed.
  doc.querySelectorAll('video').forEach(video => {
    if (!video.paused && !video.ended) recordMedia(video, 'play');
  });
  return () => removers.forEach(remove => remove());
}
